<!-- eslint-disable @typescript-eslint/no-explicit-any, no-unused-vars, @typescript-eslint/no-unused-vars, no-undef -->
<template>
  <span
      :class="['inline-block align-middle', wrapperClass]"
      :style="wrapperStyle"
      :role="role"
      :aria-label="ariaLabel || title || undefined"
      :title="title"
  >
    <span v-if="svgContent" v-html="svgContent" />
    <slot v-else name="fallback">
      <span
          class="inline-block bg-gray-200"
          :style="{ width: computedSize + 'px', height: computedSize + 'px' }"
      />
    </slot>
  </span>
</template>

<script setup lang="ts">
/* eslint-disable no-unused-vars */
import {
  defineProps,
  withDefaults,
  defineEmits,
  computed,
  onMounted,
  ref,
  watch,
} from 'vue'

type FillMap = Record<string, string>

const props = withDefaults(
    defineProps<{
      src?: string
      svg?: string
      fill?: string | FillMap
      selector?: string
      replaceExisting?: boolean
      size?: number | string
      width?: number | string
      height?: number | string
      preserveAspectRatio?: string
      wrapperClass?: string
      wrapperStyle?: Record<string, string | number>
      role?: string
      ariaLabel?: string
      title?: string
    }>(),
    {
      selector: 'path',
      replaceExisting: true,
      role: 'img',
    }
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emit = defineEmits<{
  (event: 'loaded', payload: { width?: number; height?: number; viewBox?: string | null }): void
  (event: 'error', payload: unknown): void
}>()

const svgContent = ref<string>('')

const computedSize = computed(() => {
  const s = props.size
  if (s == null) return undefined
  return typeof s === 'number' ? s : parseFloat(String(s))
})

const widthPx = computed(() => {
  if (props.width != null)
    return typeof props.width === 'number' ? `${props.width}px` : String(props.width)
  if (computedSize.value != null) return `${computedSize.value}px`
  return undefined
})

const heightPx = computed(() => {
  if (props.height != null)
    return typeof props.height === 'number' ? `${props.height}px` : String(props.height)
  if (computedSize.value != null) return `${computedSize.value}px`
  return undefined
})

function applyFillsToSvg(svgEl: SVGSVGElement) {
  const { fill, selector, replaceExisting } = props
  if (!fill) return

  const setFill = (nodeList: NodeList, color: string) => {
    nodeList.forEach((node) => {
      if (!(node instanceof SVGElement)) return
      const hasExisting = node.getAttribute('fill')
      if (!hasExisting || replaceExisting) {
        node.setAttribute('fill', color)
      }
    })
  }

  if (typeof fill === 'string') {
    const nodes = svgEl.querySelectorAll(selector || 'path')
    setFill(nodes, fill)
  } else {
    for (const [sel, color] of Object.entries(fill)) {
      if (!color) continue
      const nodes = svgEl.querySelectorAll(sel)
      setFill(nodes, color)
    }
  }
}

function applySizing(svgEl: SVGSVGElement) {
  if (widthPx.value) svgEl.setAttribute('width', widthPx.value)
  if (heightPx.value) svgEl.setAttribute('height', heightPx.value)
  if (props.preserveAspectRatio)
    svgEl.setAttribute('preserveAspectRatio', props.preserveAspectRatio)
}

function serialize(doc: Document): string {
  const svg = doc.querySelector('svg') as SVGSVGElement | null
  if (!svg) throw new Error('No <svg> root found')
  applySizing(svg)
  applyFillsToSvg(svg)
  const viewBox = svg.getAttribute('viewBox')
  const w = svg.getAttribute('width')
  const h = svg.getAttribute('height')
  emit('loaded', {
    width: w ? Number(String(w).replace('px', '')) : undefined,
    height: h ? Number(String(h).replace('px', '')) : undefined,
    viewBox,
  })
  return new XMLSerializer().serializeToString(svg)
}

async function loadAndRender() {
  try {
    let raw = props.svg?.trim()
    if (!raw && props.src) {
      if (typeof window === 'undefined') return
      const res = await fetch(props.src)
      raw = await res.text()
    }
    if (!raw) {
      svgContent.value = ''
      return
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(raw, 'image/svg+xml')

    if (doc.querySelector('parsererror')) {
      throw new Error('Invalid SVG markup provided')
    }

    svgContent.value = serialize(doc)
  } catch (err) {
    emit('error', err)
    svgContent.value = ''
  }
}

onMounted(() => {
  loadAndRender()
})

watch(
    () => [
      props.src,
      props.svg,
      props.fill,
      props.selector,
      props.replaceExisting,
      props.width,
      props.height,
      props.size,
      props.preserveAspectRatio,
    ],
    () => {
      loadAndRender()
    },
    { deep: true }
)
</script>

<style scoped>
span[role='img'] svg {
  display: inline-block;
  vertical-align: middle;
}
</style>
